import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#availabilityObserver = new MutationObserver(this.#orderAvailableFirst);
    this.#availabilityObserver.observe(this, { childList: true, subtree: true,
      attributes: true, attributeFilter: ['data-ph-state'] });
    this.#orderAvailableFirst();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#availabilityObserver?.disconnect();
    this.#availabilityRun++;
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #availabilityObserver;
  #availabilityRun = 0;

  /** Group the entire filtered collection, then slice its native page window.
   * Read server-rendered availability partitions without a collection-size cap.
   * Exclude mixed-availability products from the sold-out partition.
   */
  #orderAvailableFirst = async () => {
    const marker = this.querySelector('.ph-collection-order[data-ph-state="pending"]');
    const grid = this.querySelector('[ref="grid"]');
    if (!(marker instanceof HTMLElement) || !(grid instanceof HTMLElement)) return;

    const run = ++this.#availabilityRun;
    const requestUrl = window.location.href;
    const pageSize = Number(marker.dataset.phPageSize);
    const currentPage = Number(marker.dataset.phPage);
    const total = Number(marker.dataset.phTotal);
    const available = Number(marker.dataset.phAvailable);
    const start = (currentPage - 1) * pageSize;
    const end = Math.min(start + pageSize, total);
    marker.dataset.phState = 'loading';
    marker.textContent = marker.dataset.phLoading || '';
    grid.setAttribute('aria-busy', 'true');
    grid.inert = true;
    const isCurrent = () => this.isConnected && run === this.#availabilityRun &&
      requestUrl === window.location.href && marker.isConnected &&
      marker.dataset.phState === 'loading';

    try {
      if (!Number.isInteger(available) || available < 0 || available > total || pageSize < 1) {
        throw new Error('Invalid availability count');
      }
      const readPage = async (value, page) => {
        const url = new URL(requestUrl);
        url.searchParams.delete('filter.v.availability');
        url.searchParams.set('filter.v.availability', value);
        url.searchParams.set('page', String(page));
        url.hash = '';
        const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return { cards: [...doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]')],
          pages: Number(doc.querySelector('[ref="grid"]')?.getAttribute('data-last-page') || 0) };
      };
      const requests = [];
      const availableEnd = Math.min(end, available);
      if (availableEnd > start) {
        for (let page = Math.floor(start / pageSize) + 1; page <= Math.ceil(availableEnd / pageSize); page++) {
          const offset = (page - 1) * pageSize;
          requests.push(readPage('1', page).then(({ cards }) =>
            cards.slice(Math.max(0, start - offset), Math.min(pageSize, availableEnd - offset))));
        }
      }
      // Shopify's unavailable filter also matches mixed-availability products.
      // Remove those before slicing, so products appear exactly once globally.
      const soldStart = Math.max(0, start - available);
      const soldEnd = Math.max(0, end - available);
      if (soldEnd > soldStart) {
        requests.push((async () => {
          const sold = [];
          const seen = new Set();
          let page = 1;
          let lastPage = 1;
          do {
            const result = await readPage('0', page);
            if (!isCurrent()) return [];
            lastPage = result.pages;
            for (const card of result.cards) {
              const id = card.getAttribute('data-product-id');
              if (card.getAttribute('data-ph-available') === 'false' && !seen.has(id)) {
                seen.add(id);
                sold.push(card);
              }
            }
            page++;
          } while (sold.length < soldEnd && page <= lastPage);
          return sold.slice(soldStart, soldEnd);
        })());
      }
      const cards = (await Promise.all(requests)).flat();
      if (!isCurrent()) return;
      const ids = cards.map((card) => card.getAttribute('data-product-id'));
      if (cards.length !== end - start || new Set(ids).size !== ids.length) {
        throw new Error('Incomplete availability page');
      }
      cards.forEach((card, index) => {
        card.setAttribute('data-page', String(currentPage));
        card.className = `product-grid__item product-grid__item--${index}`;
      });
      grid.replaceChildren(...cards);
      marker.textContent = marker.dataset.phReady || '';
      marker.dataset.phState = 'ready';
    } catch (error) {
      if (!isCurrent()) return;
      marker.textContent = marker.dataset.phError || '';
      marker.dataset.phState = 'error';
      console.warn('Available-first ordering unavailable; original catalog retained.', error);
    } finally {
      if (run === this.#availabilityRun && marker.isConnected) {
        grid.removeAttribute('aria-busy');
        grid.inert = false;
      }
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
